import { motion } from "framer-motion";
import {
  ArrowLeft,
  Building2,
  Ruler,
  Shield,
  Volume2,
  Zap,
  Lightbulb,
  Weight,
  Truck,
  Smartphone,
  Sparkles,
  Eye,
  Tag,
  Wrench,
  Download,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import podV1Image from "../assets/pod-v1.png";
import { Button } from "../components/ui/button";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const specs = [
  {
    icon: Building2,
    title: "Overview",
    details: [
      "qwip pod placed in an agreed location within St Pancras",
      "Self-contained, bookable workspace - unlocked via QR code, which redirects user to our website",
      "Pay-as-you-go, on-demand. No on-site staff required",
      "For private calls, focused work, or a moment of quiet",
    ],
  },
  {
    icon: Ruler,
    title: "Pod Specs",
    details: [
      "Manufacturer: Koplus",
      "Model: Milli Stand",
      "Dimensions: W 1000mm x D 890mm x H 2145mm",
      "Net weight: 259kg",
      "Materials: steel 42.7%, glass 25.8%, aluminium 9.6%, plywood 8.8%, silicate board 8.1%, acoustic PET 3.2%",
    ],
  },
  {
    icon: Shield,
    title: "Glass & Safety",
    details: [
      "9mm laminated glass sandwich (4mm + 1mm PVB + 4mm) - shatter-resistant",
      "Class 0 fire-rated materials (spec attached)",
    ],
  },
  {
    icon: Volume2,
    title: "Acoustics & Ventilation",
    details: [
      "Certified Class B under ISO 23351-1 for speech level reduction",
      "Allows fire alarms and station announcements to be heard inside",
      "2 fans + 1 natural vent",
      "Airflow: 84 CFM (143 m³/h)",
    ],
  },
  {
    icon: Zap,
    title: "Power & Data",
    details: [
      "Input: 110-240 VAC, 50-60 Hz, 10.1 A (10 A max fuse)",
      "Basic consumption: 17 W",
      "1 x AC outlet, 1 x USB charging port, 1 x CAT-6 port",
      "Power & data cables routed from the bottom",
      "UL, PSE and CE certified components",
    ],
  },
  {
    icon: Lightbulb,
    title: "Lighting",
    details: [
      "Ceiling LED panel",
      "Motion-sensor activated on entry",
      "Configurable auto-off: 15 / 45 / 300 / 900 seconds",
    ],
  },
  {
    icon: Weight,
    title: "Weight Loading",
    details: [
      "Load carried by 4 levelling feet (one in each corner)",
      "Distribution shifts with occupant position and movement",
      "Floor protection achievable via levelling pads if required",
    ],
  },
  {
    icon: Truck,
    title: "Mobility & Install",
    details: [
      "Built-in castors for short-distance relocation",
      "Modular - can be disassembled for long-distance transport",
      "Door: right-handed standard, reversible on-site by trained staff",
    ],
  },
  {
    icon: Smartphone,
    title: "How the Space Works",
    details: [
      "Users book on-demand via the QR code, redirecting users to our booking page. They are providing a unique access code that they enter onto door handle and unlock pod.",
      "Users are charged at end of session, and are prompted to take a photo of the pod to confirm session end.",
      "No staff required on-site to facilitate bookings",
    ],
  },
  {
    icon: Sparkles,
    title: "Cleaning",
    details: ["Cleaned daily by a qwip-appointed cleaning operative"],
  },
  {
    icon: Eye,
    title: "Unattended Items",
    details: [
      "Pods are not internally monitored by CCTV",
      "Items left inside are visible from outside - glass only partially frosted",
      "Lost items reported to venue staff and handled per St Pancras lost property procedure",
      "Additional monitoring can be added subject to venue requirements",
    ],
  },
  {
    icon: Tag,
    title: "Branding & Signage",
    details: [
      "Maximum two qwip text logos, proportional and non-dominant",
      "Small qwip text logo upper-left on door",
      "Small qwip text logo centred on one side face",
      "Operating instructions sticker on front side",
      "No third-party advertising on the pods",
    ],
  },
  {
    icon: Wrench,
    title: "Maintenance",
    details: [
      "Modular design - electronic parts replaceable without relocating the unit",
      "Core parts (power, smart lock, lighting) repairable or replaceable same-day",
      "PET floor tile is replaceable",
    ],
  },
];

const StPancrasSpecs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-foreground/5 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors font-sans"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12 tablet:py-16 laptop:py-20 print:py-8">
        {/* Hero */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 tablet:mb-16 print:mb-6"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-sans mb-4 print:hidden">
            qwip x St Pancras
          </p>
          <h1 className="font-funnel font-bold text-4xl tablet:text-5xl laptop:text-6xl text-foreground leading-[0.9] tracking-tight mb-4">
            installation specs
          </h1>
          <p className="text-base tablet:text-lg text-muted-foreground font-sans max-w-md mx-auto mb-6 print:hidden">
            Technical and operational details for the temporary installation at St Pancras.
          </p>
          <a href="/qwip-st-pancras-installation-specs.pdf" download className="print:hidden">
            <Button variant="default" className="gap-2">
              <Download size={16} />
              Download PDF
            </Button>
          </a>
        </motion.div>

        {/* Pod Image */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="flex justify-center mb-14 tablet:mb-20 print:hidden"
        >
          <img
            src={podV1Image}
            alt="qwip Milli Stand pod"
            className="w-full max-w-[240px] tablet:max-w-[300px] drop-shadow-2xl object-contain"
          />
        </motion.div>

        {/* Specs Grid */}
        <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4 tablet:gap-5">
          {specs.map((spec, i) => (
            <motion.div
              key={spec.title}
              {...fadeUp}
              transition={{ duration: 0.5, delay: 0.2 + i * 0.04 }}
              className="bg-foreground/[0.03] border border-foreground/5 rounded-2xl p-5 tablet:p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <spec.icon className="w-4 h-4 text-primary" />
                </div>
                <h3 className="font-funnel font-bold text-lg text-foreground">{spec.title}</h3>
              </div>
              <ul className="space-y-1.5">
                {spec.details.map((detail) => (
                  <li
                    key={detail}
                    className="text-sm text-muted-foreground font-sans leading-relaxed flex items-start gap-2"
                  >
                    <span className="text-primary/40 mt-1.5 flex-shrink-0">·</span>
                    {detail}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Submission meta */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.75 }}
          className="mt-12 tablet:mt-16 border-t border-foreground/10 pt-8 grid grid-cols-1 tablet:grid-cols-3 gap-6 text-sm font-sans"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Point of Contact</p>
            <p className="text-foreground">Stiofán Folan-Hasici</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Contact Email</p>
            <a href="mailto:stiofan@qwip.co.uk" className="text-foreground underline underline-offset-4">
              stiofan@qwip.co.uk
            </a>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Date</p>
            <p className="text-foreground">24 June 2026</p>
          </div>
        </motion.div>

        {/* Footer CTA */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.85 }}
          className="text-center mt-14 tablet:mt-20 pb-8"
        >
          <p className="text-sm text-muted-foreground font-sans">
            Questions about the installation?{" "}
            <a href="mailto:hello@qwip.co.uk" className="text-foreground font-medium underline underline-offset-4">
              Get in touch
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default StPancrasSpecs;
